import {
  ComponentRef,
  ElementRef,
  Signal,
  TemplateRef,
  Type,
  ViewContainerRef,
  WritableSignal,
} from '@angular/core';
import { StorageTypeEnum } from '../enums/storage.enums';
import {
  TRegisteredState,
  TRegisteredValidation,
} from './validation.interface';
import { ICredentials, IUser } from './security.interfaces';
import {
  PermissionType,
  TRole,
  TPermission,
  TViewItem,
  TViewMap,
  TLoadingRequest,
  TPreferences,
  TWorkbookConfig,
  TWorkbookCompatibilityRulesConfig,
  TCellValueTypeConfig,
  TCellIndices,
} from '../types';
import { AuthenticationStepEnum, StateEnum } from '../enums';
import {
  IPageLayoutSettings,
  IToastSettings,
} from './control-settings.interfaces';
import { AksiaValidatorFn } from '../classes';
import { Observable } from 'rxjs';
import { ILoggingEvent } from './logging.interface';

type Params = {
  [key: string]: any;
};
//#region Static File Interfaces

export interface IEnvironment {
  //General
  title: string;
  production: boolean;
  version: string;
  environment_variable: string;
  storage_type: StorageTypeEnum;
  mock_use_get_only: boolean;

  //Client App URLs
  max_url: string;
  o2_url: string;
  cedric_url: string;
  origin_url: string;
  ostrako_url?: string;
  legacy_url: string;

  //API URLs
  microservices_api_url: string;
  cedric_api_url: string;
  report_service_url: string;
  mock_api_url: string;

  //Other URLs
  max_redirect_url: string;
  google_api_url: string;

  //API Keys
  google_api_key: string;
  froala_license_key: string;

  //Excel Keys
  excel_protection_key: string;

  //Security
  omit_user_groups: boolean;
  mock_user: unknown;

  reset_password_token_valid_minutes: number;
}

//#endregion

//#region Service Interfaces

export interface ILoggingService {
  handleError(event: ILoggingEvent): void;
  handleMessage(event: ILoggingEvent): void;
}

export interface INavigationService {
  goToPage: (url: string, newTab?: boolean) => void;
  goToExternalPage: (url: string, newTab?: boolean) => void;
  addQueryParams(params: Params): void;
  removeQueryParams(params: Params): void;
  getQueryParamValue(paramName: string): string | null;
  getPreviousUrl(): string;
}

export interface ILoadingService {
  loadRequests: WritableSignal<Map<string, Array<TLoadingRequest>>>;
  isLoading: WritableSignal<boolean>;
  loadingPercentage?: Signal<string | undefined>;
  pageLoading: Signal<string | undefined>;
  areaLoading: (key: string) => Signal<string | undefined>;
  registerLoadingKey: (key: string) => void;
  addLoadRequest: (key: string, options?: TLoadingRequest) => void;
  removeLoadRequest: (key: string) => void;
  resetLoadRequests: () => void;
  setLoadResponse: (key: string, title?: string) => void;
}

export interface IAuthenticationService {
  //readonly user: Signal<IUser | undefined>;
  //readonly isAuthenticated: Signal<boolean>;
  readonly authenticationStep: Signal<AuthenticationStepEnum>;
  readonly statusMessage: Signal<string>;
  readonly redirectUrl: WritableSignal<string | (() => string) | undefined>;
  readonly signIn: (credentials?: ICredentials) => void;
  readonly signOut: () => void;
  readonly verify2FA?: (credentials?: ICredentials) => void;
  //readonly impersonate?: (role: TRole | unknown) => void;
}

export interface IUserRoleService {
  readonly activeUser: Signal<IUser | undefined>;
  readonly activeRole: Signal<TRole | undefined>;
  readonly userRolesInfo?: Signal<string>;
  readonly preferences: Signal<TPreferences | undefined>;
  readonly impersonates: Signal<TRole | undefined>;
  readonly isAuthenticated: Signal<boolean>;
  readonly redirectAfterLogin: WritableSignal<string | undefined>;
  readonly initActiveUser: (user: IUser) => void;
  readonly setActiveUser: (user: IUser) => void;
  readonly clearActiveUser: () => void;
  readonly isInRole: (role: TRole) => boolean;
  readonly isAdmin: () => boolean;
  readonly impersonate: (role: TRole | unknown) => void;
  /* readonly loadUserPreferences: () => void; */
  readonly saveUserPreferences: (preferences: Partial<TPreferences>) => void;
}

export interface IAnchorService {
  layoutIsReady: Signal<boolean>;
  anchors: Signal<
    ReadonlyArray<{ tag: string; ref: ElementRef; offset: number }>
  >;
  jumpToAnchor: (anchorEl?: ElementRef, offset?: number) => void;
}

export interface ILayoutService<
  ViewCategory,
  View,
  ViewItem extends TViewItem<unknown>,
  ViewMap extends TViewMap<ViewCategory, View>,
> {
  layoutIsReady: Signal<boolean>;
  layoutSettings: Signal<IPageLayoutSettings | undefined>;
  viewCategory: WritableSignal<ViewCategory | undefined>;
  viewMap: Signal<ViewMap | undefined>;
  defaultView: Signal<View | undefined>;
  filteredViews: Signal<Array<View> | undefined>;
  selectedView: Signal<View | undefined>;
  selectedViewItems: Signal<Array<ViewItem> | undefined>;
  availableViewItems: Signal<Array<ViewItem> | undefined>;
  togglePermissions: WritableSignal<boolean>;
  permissions: WritableSignal<Array<TPermission> | undefined>;
  editPermissionsForControls: WritableSignal<string | undefined>;
  editingPermission: WritableSignal<TPermission | undefined>;
  editingClaims: Signal<
    | Array<{
        views?: Array<string>;
        roles: Array<TRole>;
        categories?: Array<string>;
        rights: PermissionType;
      }>
    | undefined
  >;
  anchors: Signal<Array<{ tag: string; ref: ElementRef; offset: number }>>;
  selectView: (view: View) => void;
  setDefaultView: (view: View, store?: boolean) => void;
  downloadView: () => void;
  getPermissionFor: (
    control: string,
    tag: string,
  ) => PermissionType | undefined;
  editPermissionFor: (control: string, tag: string) => void;
  addClaim: () => void;
  removeClaim: (index: number) => void;
  applyClaimToCollection: () => void;
  downloadPermissions: () => void;
  addAnchorhost: (host: ElementRef) => void;
  addAnchor: (tag: string, ref: ElementRef, offset?: number) => void;
  clearAnchors: () => void;
  refreshAnchors: () => void;
  jumpToAnchor: (anchorEl?: ElementRef, offset?: number) => void;
}

export interface IPermissionService {
  permissions: Signal<unknown>;
  selectedView: Signal<unknown>;
  editPermissionsForControls: Signal<string | undefined>;
  getPermissionFor: (
    control: string,
    tag: string,
  ) => PermissionType | undefined;
  editPermissionFor: (control: string, tag: string) => void;
}

export interface IStateService {
  stateExists: (tag: string) => boolean;
  registerState: (
    tag: string,
    value: unknown,
    state?: StateEnum,
    comparer?: (a: any, b: any) => boolean,
    audited?: boolean,
  ) => void;
  removeState: (tag: string, hardDelete?: boolean) => void;
  clearStates: () => void;
  resetState: (tag: string, onlyDirty?: boolean) => void;
  updateState: (tag: string, value: unknown) => void;
  getState: (tag: string) => StateEnum | undefined;
  getRegisteredState: (tag: string) => TRegisteredState | undefined;
  hasDirty: WritableSignal<boolean>;
  hasDirtyAndAudited: WritableSignal<boolean>;
  state: WritableSignal<StateEnum>;
  summary: WritableSignal<string>;
}

export interface IValidationService<T = unknown> {
  registeredValidations: Map<string, TRegisteredValidation>;
  validationExists: (tag: string) => boolean;
  registerValidation: (
    tag: string,
    value: Signal<unknown>,
    validators: Array<AksiaValidatorFn>,
    chainTags?: Signal<Array<string>> | Array<string>,
  ) => TRegisteredValidation;
  removeValidation: (tag: string) => void;
  clearValidations: () => void;
  updateValidationTag: (currentTag: string, newTag: string) => void;
  validate: (tag: string, value: unknown) => TRegisteredValidation;
  validateAll: (tag?: string) => void;
  getValidationState(tag: string): StateEnum;
  getErrorDetailsByTag: (
    tag: string,
  ) => Array<{ tag: string; messages: Array<string> }>;
  hasErrors: WritableSignal<boolean>;
  hasWarnings: WritableSignal<boolean>;
  state: Signal<StateEnum>;
  summary: WritableSignal<string>;
  details: WritableSignal<string>;
}

export interface IInitService {
  init(): Observable<any>;
}

export interface IAssetService {
  collectionResource$: Observable<Map<string, unknown>>;
  collection: Signal<Map<string, unknown> | undefined>;
}

export interface IToasterService {
  success: (
    message: string,
    options?: Partial<Omit<IToastSettings, 'type' | 'message'>>,
  ) => void;

  error: (
    message: string,
    options?: Partial<Omit<IToastSettings, 'type' | 'message'>>,
  ) => void;

  info: (
    message: string,
    options?: Partial<Omit<IToastSettings, 'type' | 'message'>>,
  ) => void;

  warning: (
    message: string,
    options?: Partial<Omit<IToastSettings, 'type' | 'message'>>,
  ) => void;
}

export interface IModalService {
  openModal: (settings: {
    label?: string;
    text?: string;
    hasBackdrop?: boolean;
    component?: Type<any>;
    componentInput?: Record<string, unknown>;
    template?: TemplateRef<any>;
    templateContext?: any;
    templateContextMore?: any;
    vcRef?: ViewContainerRef;
    modalloading?: string;
    actions?: Array<{
      label: string;
      action: () => void;
      icon?: string;
      class?: string;
      disabled?: boolean;
    }>;
    textFormatter?: 'plain' | 'json' | 'html';
    contentCssClass?: string;
    onModalOpen?: () => void;
  }) => ComponentRef<any>;

  openWarning: (settings: {
    label: string;
    text: string;
    actions?: Array<{
      label: string;
      action: () => void;
      icon?: string;
      class?: string;
      disabled?: boolean;
    }>;
    vcRef?: ViewContainerRef;
  }) => void;

  closeModal: (label: string) => void;
}

export interface IMetaDataService {
  createNote: (
    value: string,
    field: string,
    entityId?: number,
    entityTypeId?: number,
  ) => any;
  getNote: (
    field: string,
    entityId?: number,
    entityTypeId?: number,
  ) => any | undefined;
  updateNote(item: any): void;
  setNoteContent(
    content: string,
    field: string,
    entityId?: number,
    entityTypeId?: number,
  ): void;
  getNoteContent(
    field: string,
    entityId?: number,
    entityTypeId?: number,
  ): string | undefined;
  hasNoteContent(
    field: string,
    entityId?: number,
    entityTypeId?: number,
  ): boolean;
}

export interface IExcelService {
  loadFile: (file: File | FileList | null) => Signal<boolean>;
  saveFile: (fileName: string, map: TWorkbookConfig) => void;
  resetFile: () => void;
  isFileCompatible: (
    workbookOptions: Partial<TWorkbookCompatibilityRulesConfig>,
    rules: Partial<TWorkbookCompatibilityRulesConfig>,
  ) => boolean;
  workbookIsLoaded: Signal<boolean>;
  workbook: any | null;
  getSheetByName: (name: string) => any;
  getSheetNames: (workbook: any) => Array<string>;
  getTableByName: (sheetName: string, tableName: string) => any;
  getTableData: <T, U>(
    sheetName: string,
    tableName: string,
    options?: {
      skipHeader?: boolean;
      skipTotals?: boolean;
      fieldMapping?: { [key: string]: keyof U };
      fnMapping?: { [key: string]: ((value: any) => any) | undefined };
    },
  ) => Array<U> | undefined;
  getCellRangeByName(name: string): TCellIndices | undefined;
  getCellTypeByRowCol(
    sheetName: string,
    row: number,
    col: number,
  ): TCellValueTypeConfig | undefined;
  getCellTypeByName(
    sheetName: string,
    name: string,
  ): TCellValueTypeConfig | undefined;
  getCellValueByName<T>(name: string): T | undefined;
  getCellValueByRowCol<T>(
    sheetName: string,
    row: number,
    col: number,
  ): T | undefined;
  getCellNoteByName(name: string): string | undefined;
  getCellNoteByRowCol(
    sheetName: string,
    row: number,
    col: number,
  ): string | undefined;
  printCellConfigsFromCells(sheetName: string, index: number): void;
}

export interface IBlobStorageService {
  uploadFile(
    containerName: string,
    blobName: string,
    blobData: unknown,
  ): Promise<string>;

  listBlobs(containerName: string): Promise<string[]>;

  downloadBlobAsText(containerName: string, blobName: string): Promise<string>;
}

//#endregion
